import { z } from 'zod';
import type { TeamRole } from './roles.js';

// ---------- Time zones ----------

/** Where a team trains, so "2 hours before" means the right moment. Sydney to start with. */
export const TEAM_TIME_ZONES = [
  { id: 'Australia/Sydney', label: 'Sydney, Canberra (NSW, ACT)' },
  { id: 'Australia/Melbourne', label: 'Melbourne (VIC)' },
  { id: 'Australia/Brisbane', label: 'Brisbane (QLD)' },
  { id: 'Australia/Adelaide', label: 'Adelaide (SA)' },
  { id: 'Australia/Darwin', label: 'Darwin (NT)' },
  { id: 'Australia/Perth', label: 'Perth (WA)' },
  { id: 'Australia/Hobart', label: 'Hobart (TAS)' },
  { id: 'Pacific/Auckland', label: 'Auckland (NZ)' },
] as const;
export type TeamTimeZone = (typeof TEAM_TIME_ZONES)[number]['id'];
export const DEFAULT_TIME_ZONE: TeamTimeZone = 'Australia/Sydney';
export const teamTimeZone = z.enum(TEAM_TIME_ZONES.map((z) => z.id) as [TeamTimeZone, ...TeamTimeZone[]], {
  errorMap: () => ({ message: 'Choose a time zone from the list.' }),
});

// ---------- Reminders ----------

/** How long before a session the reminder goes, in minutes. */
export const REMINDER_LEADS = [30, 60, 120, 240, 1440] as const;
export type ReminderLead = (typeof REMINDER_LEADS)[number];
export const REMINDER_LEAD_LABELS: Record<ReminderLead, string> = {
  30: '30 minutes before',
  60: '1 hour before',
  120: '2 hours before',
  240: '4 hours before',
  1440: 'The day before',
};
const reminderLead = z.union(REMINDER_LEADS.map((n) => z.literal(n)) as unknown as [z.ZodLiteral<ReminderLead>, z.ZodLiteral<ReminderLead>, ...z.ZodLiteral<ReminderLead>[]], {
  errorMap: () => ({ message: 'Choose how long before the session.' }),
});

/** Until someone chooses: coaches 2 hours before, players and parents the day before, read-only members off. */
export function defaultReminder(role: TeamRole): { enabled: boolean; leadMinutes: ReminderLead } {
  if (role === 'player' || role === 'parent') return { enabled: true, leadMinutes: 1440 };
  if (role === 'viewer') return { enabled: false, leadMinutes: 120 };
  return { enabled: true, leadMinutes: 120 };
}

/** Your reminder setting for one team. */
export interface TeamReminder {
  enabled: boolean;
  leadMinutes: ReminderLead;
  /** True until you've chosen (the default for your role applies). */
  isDefault: boolean;
  timeZone: TeamTimeZone;
}

export const teamReminderInput = z.object({ enabled: z.boolean(), leadMinutes: reminderLead });
export type TeamReminderInput = z.infer<typeof teamReminderInput>;

/** Your reminder for one session: the team setting, or a change just for this session. */
export interface SessionReminder {
  team: { enabled: boolean; leadMinutes: ReminderLead };
  /** 'team' follows the team setting; 'off' or 'custom' are changes for this session only. */
  mode: 'team' | 'off' | 'custom';
  enabled: boolean;
  leadMinutes: ReminderLead;
  /** When it goes (ISO), if it's on and the session has a time. */
  sendsAt: string | null;
  /** Already sent for this session time. */
  sent: boolean;
  /** Players and parents only get reminders for published sessions. */
  waitingForPublish: boolean;
  timeZone: TeamTimeZone;
}

export const sessionReminderInput = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('team') }),
  z.object({ mode: z.literal('off') }),
  z.object({ mode: z.literal('custom'), leadMinutes: reminderLead }),
]);
export type SessionReminderInput = z.infer<typeof sessionReminderInput>;

// ---------- Times ----------

/** The UTC instant for a wall-clock date and time in a time zone (handles daylight saving). */
export function zonedToUtc(date: string, time: string, timeZone: string): Date {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const [h, mi] = time.split(':').map(Number) as [number, number];
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Guess, then correct by the zone's offset at that moment (twice, for the change-over days).
  let t = wall - offsetMs(wall, timeZone);
  t = wall - offsetMs(t, timeZone);
  return new Date(t);
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();
function offsetMs(at: number, timeZone: string) {
  let f = fmtCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    fmtCache.set(timeZone, f);
  }
  const p = Object.fromEntries(f.formatToParts(new Date(at)).map((x) => [x.type, x.value]));
  const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
  return asUtc - Math.floor(at / 1000) * 1000;
}
